import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type ReserveTrip, type Person } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconTrips } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { personName, dateTime } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer } from '@/components/admin/Drawer';
import { TripDrawer } from '@/components/admin/TripDrawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

const PAGE = 25;
// Telride Boats trips are trip_type=15 — reserve-class, admin-assigned-only.
const BOATS_TRIP_TYPE = 15;

type ReleaseFilter = '' | 'upcoming' | 'released';

const STATUS: Record<number, { label: string; tone: BadgeTone }> = {
  1: { label: 'Requested', tone: 'info' },
  2: { label: 'Arrived', tone: 'info' },
  3: { label: 'Started', tone: 'warn' },
  5: { label: 'Completed', tone: 'ok' },
  6: { label: 'Cancelled', tone: 'danger' },
  7: { label: 'Cancelled (driver)', tone: 'danger' },
};

/** Boat bookings — Telride Boats (trip-svc trip_type=15): dock-to-dock charters,
 *  reserve-class and ADMIN-ASSIGNED-ONLY. Like Reserve, a Boats booking is HELD
 *  at creation; an operator OFFERS it to a boat-capable captain (reserves.assign,
 *  same proxy to trip-svc admin-assign) who accepts/rejects it in their app and
 *  gets an FCM — appearing as "Boat Ride" in their Bookings. driver_id is set
 *  only on accept; until then it shows "Offered (pending)". Mirrors the Reserve
 *  page; the only differences are the trip_type filter + a passengers column. */
export function BoatTrips() {
  const [q, setQ] = useState('');
  const [released, setReleased] = useState<ReleaseFilter>('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [assigning, setAssigning] = useState<ReserveTrip | null>(null);
  const canAssign = useAdminPermission('reserves.assign');

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'boats', q, released, offset],
    queryFn: ({ signal }) =>
      adminApi.reserves(
        { q, released: released === '' ? undefined : released, limit: PAGE, offset, trip_type: BOATS_TRIP_TYPE },
        signal,
      ),
    placeholderData: (prev) => prev,
  });

  const unavailable = data && data.available === false;

  const columns: Array<Column<ReserveTrip>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    { key: 'trip_id', header: 'Booking', cell: (r) => <span className="text-slate-200">{r.trip_id || '—'}</span> },
    { key: 'cust', header: 'Customer', cell: (r) => <span className="metric text-xs text-slate-300">#{r.customer_id}</span> },
    {
      key: 'pax',
      header: 'Pax',
      align: 'right',
      cell: (r) => <span className="metric text-xs text-slate-300">{r.number_of_people || '—'}</span>,
    },
    {
      key: 'drv',
      header: 'Captain',
      cell: (r) =>
        r.driver_id ? (
          <span className="metric text-xs text-slate-300">#{r.driver_id}</span>
        ) : r.assigned_driver_id ? (
          <Badge tone="info" dot>Offered #{r.assigned_driver_id} · pending</Badge>
        ) : (
          <Badge tone="warn">Unassigned</Badge>
        ),
    },
    {
      key: 'pickup_at',
      header: 'Pickup scheduled',
      cell: (r) => <span className="text-xs text-slate-300">{dateTime(r.scheduled_pickup_at)}</span>,
    },
    { key: 'pickup', header: 'From dock', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.pickup_address}>{r.pickup_address || '—'}</span> },
    { key: 'drop', header: 'To dock', cell: (r) => <span className="truncate text-xs text-slate-300" title={r.drop_address}>{r.drop_address || '—'}</span> },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => {
        const s = STATUS[r.status] ?? { label: `#${r.status}`, tone: 'neutral' as BadgeTone };
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    { key: 'total', header: 'Total', align: 'right', cell: (r) => <span className="metric text-xs">{r.total ? `$${r.total.toFixed(2)}` : '—'}</span> },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => {
        const assignable = !r.released && !r.driver_id && !r.assigned_driver_id;
        return (
          <div className="flex justify-end gap-2">
            {canAssign && assignable ? (
              <button
                type="button"
                onClick={() => setAssigning(r)}
                className="rounded-md border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-xs font-medium text-brand-300 hover:bg-brand-500/20"
              >
                Assign
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setSelected(r.id)}
              className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
            >
              View
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Boat bookings"
        subtitle="Telride Boats — dock-to-dock charters. Held at booking; assign a boat captain who accepts or rejects in their app."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search boat bookings…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Boat bookings not yet available"
            description={data?.note ?? 'The trips table is not in this Postgres yet.'}
            icon={<IconTrips className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between gap-3">
            <ReleaseFilterControl value={released} onChange={(v) => { setReleased(v); setOffset(0); }} fetching={isFetching} />
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading boat bookings…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load boat bookings" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<ReserveTrip>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No boat bookings"
                      description="No Telride Boats (trip_type=15) bookings match this filter yet."
                      icon={<IconTrips className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <TripDrawer id={selected} onClose={() => setSelected(null)} /> : null}
      {assigning ? <AssignCaptainModal booking={assigning} onClose={() => setAssigning(null)} /> : null}
    </AdminPage>
  );
}

/** AssignCaptainModal — search drivers and OFFER the boat booking to one boat
 *  captain. On success the booking flips to "Offered (pending)" and the captain
 *  gets an FCM + a Bookings entry ("Boat Ride") to accept/reject. (Same assign
 *  proxy as Reserve; the captain must be boat-capable to actually run it.) */
function AssignCaptainModal({ booking, onClose }: { booking: ReserveTrip; onClose: () => void }) {
  const qc = useQueryClient();
  const [dq, setDq] = useState('');
  const [picked, setPicked] = useState<Person | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['admin', 'drivers', 'picker', dq],
    queryFn: ({ signal }) => adminApi.drivers({ q: dq, limit: 8, offset: 0 }, signal),
    placeholderData: (prev) => prev,
  });
  const drivers = data?.items ?? [];

  const mutation = useMutation({
    mutationFn: () => adminApi.assignReserveDriver(booking.id, picked!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'boats'] });
      onClose();
    },
  });

  return (
    <Drawer
      title={`Assign boat booking ${booking.trip_id || `#${booking.id}`}`}
      subtitle="Offer this charter to a boat captain — they accept or reject it in their app."
      onClose={onClose}
    >
      <div className="space-y-4">
        <SearchBox value={dq} onChange={setDq} placeholder="Search captains by name / phone / id…" />
        <p className="text-[11px] text-muted">Tip: assign a driver you've flagged boat-capable with an active vessel.</p>

        <Card flush>
          {isFetching && drivers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Searching drivers…" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-muted">No drivers match.</div>
          ) : (
            <ul className="divide-y divide-ink-800">
              {drivers.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setPicked(d)}
                    className={
                      'flex w-full items-center justify-between px-4 py-2.5 text-left hover:bg-ink-800 ' +
                      (picked?.id === d.id ? 'bg-brand-500/10' : '')
                    }
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-slate-100">{personName(d)}</span>
                      <span className="metric block text-[11px] text-muted">
                        #{d.id} · {d.country_code}{d.phone}
                      </span>
                    </span>
                    {picked?.id === d.id ? <Badge tone="ok">Selected</Badge> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={!picked || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="btn-primary"
          >
            {mutation.isPending ? 'Assigning…' : picked ? `Offer to ${personName(picked)}` : 'Pick a captain'}
          </button>
          {mutation.isError ? (
            <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}

function ReleaseFilterControl({
  value,
  onChange,
  fetching,
}: {
  value: ReleaseFilter;
  onChange: (v: ReleaseFilter) => void;
  fetching?: boolean;
}) {
  const opts: Array<{ key: ReleaseFilter; label: string }> = [
    { key: '', label: 'All' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'released', label: 'Past/Released' },
  ];
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-lg border border-ink-700 bg-ink-900 p-0.5">
        {opts.map((o) => (
          <button
            key={o.key || 'all'}
            type="button"
            onClick={() => onChange(o.key)}
            className={
              'rounded-md px-3 py-1 text-xs font-medium transition-colors ' +
              (value === o.key
                ? 'bg-brand-500 text-white'
                : 'text-text-secondary hover:bg-hover hover:text-slate-100')
            }
          >
            {o.label}
          </button>
        ))}
      </div>
      {fetching ? <Spinner /> : null}
    </div>
  );
}
